// Centre de notifications — cloche + panneau. Monté dans la topbar
// back-office (Header) et dans l'en-tête de l'Espace employé, avec
// les mêmes classes nc- (styles globaux, tokens de tokens.css).
//
// « Voir comme » : pendant une impersonation le serveur refuse toute
// écriture (403 impersonation_read_only), donc les contrôles d'écriture
// sont masqués et un clic ne marque rien comme lu — la liste reste
// consultable, c'est bien l'inbox de la cible.
import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { AuthMeResponse, NotificationDTO } from '@pointage/shared';
import {
  useMarkNotifsRead, useNotifications, usePushSubscription, useSubscribePush,
  useUnreadNotifCount, useUnsubscribePush, useVapidKey,
} from '../api/hooks.js';
import { pushPermission, pushSupported } from '../lib/push.js';
import '../styles/notifications.css';

interface Props {
  me: AuthMeResponse;
}

const RTF = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

function relTime(iso: string): string {
  const secs = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(secs);
  for (const [unit, size] of UNITS) {
    if (abs >= size) return RTF.format(Math.round(secs / size), unit);
  }
  return "à l'instant";
}

export function NotificationBell({ me }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const unread = useUnreadNotifCount();
  const list = useNotifications(open);
  const markRead = useMarkNotifsRead();
  const readOnly = me.impersonating !== null;

  // Push navigateur : opt-in explicite, par navigateur. Le contrôle
  // n'existe que si le navigateur sait faire ET que le serveur est
  // configuré (clés VAPID) ET hors impersonation (écriture bloquée).
  const supported = pushSupported();
  const vapid = useVapidKey();
  const pushSub = usePushSubscription(open && supported);
  const subscribe = useSubscribePush();
  const unsubscribe = useUnsubscribePush();
  const permission = pushPermission();
  const showPushRow = supported && !readOnly && !!vapid.data;

  // Fermeture au clic extérieur + Échap (même pattern que UserMenu).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pages = list.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  // Le compteur du panneau ouvert prime : il vient de la même réponse
  // que la liste affichée.
  const count = pages[0]?.unreadCount ?? unread.data ?? 0;

  const onItem = (n: NotificationDTO) => {
    if (!n.readAt && !readOnly) markRead.mutate({ ids: [n.id] });
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <div className="nc-wrap" ref={ref}>
      <button
        type="button"
        className="icon-button nc-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Notifications (${count} non lues)` : 'Notifications'}
        data-testid="notif-bell"
      >
        <Bell aria-hidden="true" />
        {count > 0 && (
          <span className="nc-badge" data-testid="notif-badge">{count > 9 ? '9+' : count}</span>
        )}
      </button>
      {open && (
        <div className="nc-panel" role="dialog" aria-label="Notifications" data-testid="notif-panel">
          <div className="nc-head">
            <h3>Notifications</h3>
            {!readOnly && count > 0 && (
              <button type="button" onClick={() => markRead.mutate({ all: true })} data-testid="notif-mark-all">
                Tout marquer lu
              </button>
            )}
          </div>
          {items.length === 0 && !list.isLoading && <div className="nc-empty">Aucune notification</div>}
          {items.map((n) => (
            <button type="button" key={n.id} className="nc-item" onClick={() => onItem(n)}>
              <span className={'nc-dot' + (n.readAt ? ' nc-read' : '')} aria-hidden="true" />
              <span className="nc-body">
                <span className="nc-title">{n.title}</span>
                {n.body && <span className="nc-sub">{n.body}</span>}
                <span className="nc-time">{relTime(n.createdAt)}</span>
              </span>
            </button>
          ))}
          {list.hasNextPage && (
            <button
              type="button"
              className="nc-more"
              onClick={() => void list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
            >
              Voir plus
            </button>
          )}
          {showPushRow && (
            <div className="nc-push" data-testid="notif-push-row">
              <span className="nc-push-label">Notifications du navigateur</span>
              {permission === 'denied' ? (
                <span className="nc-push-blocked">Bloquées par le navigateur</span>
              ) : (
                <button
                  type="button"
                  className="nc-push-btn"
                  disabled={subscribe.isPending || unsubscribe.isPending}
                  onClick={() =>
                    pushSub.data
                      ? unsubscribe.mutate()
                      : subscribe.mutate(vapid.data as string)
                  }
                  data-testid="notif-push-toggle"
                >
                  {pushSub.data ? 'Désactiver' : 'Activer'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
